import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, apiError } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=user
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyAuth = (data) => {
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_otp) return data;
      return applyAuth(data);
    } catch (e) { setError(apiError(e)); throw e; }
  };

  const registerStart = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register/start", payload);
      return data;
    } catch (e) { setError(apiError(e)); throw e; }
  };

  const registerVerify = async (challenge_id, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register/verify", { challenge_id, code });
      return applyAuth(data);
    } catch (e) { setError(apiError(e)); throw e; }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
  };

  const STAFF_ROLES = ["platform_owner", "admin", "owner", "superadmin", "staff", "engineer"];
  const isStaff = !!user && (
    STAFF_ROLES.includes(String(user.role || "").toLowerCase().trim()) ||
    user.is_staff === true || user.is_admin === true
  );
  const accountLevel = user?.account_level || "personal";
  const isBusiness = !!user && (user.has_org || user.business_entitled || user.role === "org_admin" || isStaff);

  return (
    <AuthCtx.Provider value={{ user, error, setError, login, registerStart, registerVerify, logout, refresh: load, isStaff, accountLevel, isBusiness, applyAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}
